import { RedoBadge } from "@redotech/redo-web/arbiter-components/badge/redo-badge";
import { NodeProps, useStore } from "@xyflow/react";
import { memo } from "react";
import * as styles from "./phase-divider-node.module.css";

export interface PhaseDividerNodeData {
  label: string;
  width?: number;
  [key: string]: unknown;
}

const DEFAULT_WIDTH = 2400;
const DASH_STROKE = "#98a2b3";
// Extra logical-px beyond the visible viewport on each side so the line never
// ends exactly at the screen edge when the user is mid-pan.
const VIEWPORT_BUFFER = 400;

/**
 * Non-interactive React Flow node rendering a horizontal dashed line with a
 * centered phase label. Inserted between phase bands in Compensation-style
 * flows so each phase's sub-graph reads as its own swimlane.
 *
 * Two viewport-aware behaviors keep the swimlane usable across the full
 * zoom range:
 *
 * - The line is sized and positioned dynamically from the React Flow
 *   viewport so it always spans the visible canvas regardless of zoom or
 *   pan. A static `data.width` would shrink on screen as the user zooms out.
 * - The phase badge counter-scales when zoomed out so its on-screen size is
 *   constant at zoom <= 1 (stays readable from any zoom level). When zoomed
 *   in past 1.0 we let it grow with the canvas — no point fighting the user.
 */
export const PhaseDividerNode = memo(function PhaseDividerNode({
  data,
  positionAbsoluteX,
}: NodeProps & { data: PhaseDividerNodeData; positionAbsoluteX: number }) {
  const labelWidth = data.width ?? DEFAULT_WIDTH;
  // Read each primitive separately so React Flow's store uses default
  // `Object.is` equality and skips re-renders when an unrelated piece of
  // state changes. A single object selector would allocate a new object
  // every store update and re-render on every change.
  const panX = useStore((s) => s.transform[0]);
  const zoom = useStore((s) => s.transform[2]);
  const canvasWidth = useStore((s) => s.width);

  // Map screen edges into the flow's logical coordinate system.
  const logicalLeftScreenEdge = -panX / zoom;
  const logicalRightScreenEdge = (canvasWidth - panX) / zoom;

  const lineWidth =
    logicalRightScreenEdge - logicalLeftScreenEdge + 2 * VIEWPORT_BUFFER;
  // The outer divider sits at logical x = positionAbsoluteX. Shift the
  // absolutely-positioned line left/right so it starts just outside the
  // viewport's left edge — keeps the label centered on the divider while the
  // line spans edge-to-edge.
  const lineLeft = logicalLeftScreenEdge - VIEWPORT_BUFFER - positionAbsoluteX;

  return (
    <div className={styles.divider} style={{ width: labelWidth }}>
      <svg
        className={styles.lineSvg}
        height={2}
        preserveAspectRatio="none"
        style={{ left: lineLeft, width: lineWidth }}
        viewBox={`0 0 ${lineWidth} 2`}
        width={lineWidth}
      >
        <line
          stroke={DASH_STROKE}
          strokeDasharray="6 6"
          strokeWidth={1.5}
          x1={0}
          x2={lineWidth}
          y1={1}
          y2={1}
        />
      </svg>
      <div
        className={styles.labelWrapper}
        // Counter-scale the label when zoomed out so it stays at its zoom=1
        // on-screen size and remains readable. Only floor the scale; when
        // zoomed in (zoom > 1) we let the badge grow normally with the canvas
        // so we don't fight the user.
        style={{ transform: `scale(${Math.max(1, 1 / zoom)})` }}
      >
        <RedoBadge color="gray" size="sm" text={data.label} />
      </div>
    </div>
  );
});
